import type { StoreApi } from 'zustand'
import type { createAuthApiClient } from '../infrastructure/authApiClient'
import type { SetPasswordDetails, SetPasswordResult, SetPasswordStore } from './types'
import type { Logger } from '@/scopes/common/domain/types'
import type { ConfigurationClient } from '@/scopes/common/domain/types/configuration'
import type { LocationResolver } from '@/scopes/common/infrastructure/dom/location'
import { getPostAuthRedirectUrl, toError } from './actions'

interface SetPasswordActionDependencies {
  apiClient: ReturnType<typeof createAuthApiClient>
  store: StoreApi<SetPasswordStore>
  configurationClient: ConfigurationClient
  location: LocationResolver
  logger: Logger
}

export function createSetPasswordActions({
  apiClient,
  store,
  configurationClient,
  location,
  logger,
}: SetPasswordActionDependencies) {
  function redirectToLoginPage() {
    location.redirectTo(configurationClient.getField('links.login'))
  }

  function showExpiredLink() {
    store.getState().setIn('linkError', 'expired')
    store.getState().setIn('pageInitializationStatus', 'success')
  }

  async function pageLoaded() {
    const state = store.getState()

    if (state.pageInitializationStatus !== 'idle')
      return

    state.setIn('submitError', null)
    state.setIn('pageInitializationStatus', 'loading')

    if (!state.token) {
      showExpiredLink()
      return
    }

    let isTokenValid: boolean

    try {
      isTokenValid = await apiClient.validatePasswordResetToken(state.token)
    }
    catch (error) {
      logger.logError(toError(error), { action: 'pageLoaded' })
      store.getState().setIn('linkError', 'validationFailed')
      store.getState().setIn('pageInitializationStatus', 'error')
      return
    }

    if (!isTokenValid) {
      showExpiredLink()
      return
    }

    store.getState().setIn('pageInitializationStatus', 'success')
  }

  async function setPasswordRequested(details: SetPasswordDetails) {
    const state = store.getState()

    if (state.submitStatus === 'loading' || state.pageInitializationStatus !== 'success')
      return

    state.setIn('submitError', null)
    state.setIn('submitStatus', 'loading')

    if (!state.token) {
      state.setIn('submitError', { type: 'unexpectedError' })
      state.setIn('submitStatus', 'error')
      return
    }

    let result: SetPasswordResult

    try {
      result = await apiClient.setPassword(state.token, details)
    }
    catch (error) {
      logger.logError(toError(error), { action: 'setPasswordRequested' })
      store.getState().setIn('submitError', { type: 'unexpectedError' })
      store.getState().setIn('submitStatus', 'error')
      return
    }

    if (result === 'signInFailed') {
      store.getState().setIn('submitStatus', 'success')
      return
    }

    location.replaceWith(getPostAuthRedirectUrl(configurationClient, location, 'passwordSet'))
  }

  function loginPageRequested() {
    redirectToLoginPage()
  }

  return {
    pageLoaded,
    setPasswordRequested,
    loginPageRequested,
  }
}
